/**
 * The generative summary of the day, drawn with json-render (@json-render/react) inside « Ta journée », the brief
 * bubble at the top of today's conversation: the registry gives each component of the shared catalog
 * (src/shared/pulse-summary.ts) its compact Nuit look, and the two actions their behaviour — `ask` puts a message in
 * the conversation's composer (lib/chat-handoff), `open` goes to an in-app page from the catalog's closed list.
 */
import { useNavigate } from "@tanstack/react-router";
import { defineRegistry, JSONUIProvider, Renderer } from "@json-render/react";
import type { Spec } from "@json-render/core";
import { useCallback, useMemo, type ReactNode } from "react";
import { SUMMARY_ROUTES, summaryCatalog, type PulseSummary, type SummaryRoute } from "../../shared/pulse-summary";
import { composeInChat } from "../lib/chat-handoff";
import { cx } from "./ui";

type Tone = "neutral" | "accent" | "warning" | "success" | null | undefined;

const STAT_TONE: Record<string, string> = {
  neutral: "text-fg",
  accent: "text-accent",
  warning: "text-warning",
  success: "text-success",
};

/** The sky of a Weather element: a drawn glyph (never an emoji on screen) and its name. */
export const SKY: Record<string, { label: string; d: string }> = {
  sun: { label: "Soleil", d: "M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" },
  cloud: { label: "Nuageux", d: "M7 18a4 4 0 0 1-.6-8A6 6 0 0 1 18 9.5a4.3 4.3 0 0 1-.5 8.5z" },
  rain: { label: "Pluie", d: "M7 15a4 4 0 0 1-.6-8A6 6 0 0 1 18 6.5a4.3 4.3 0 0 1-.5 8.5zM8 18l-1 3M12 18l-1 3M16 18l-1 3" },
  storm: { label: "Orage", d: "M7 14a4 4 0 0 1-.6-8A6 6 0 0 1 18 5.5a4.3 4.3 0 0 1-.5 8.5zM12 14l-2 4h4l-2 4" },
  snow: { label: "Neige", d: "M7 14a4 4 0 0 1-.6-8A6 6 0 0 1 18 5.5a4.3 4.3 0 0 1-.5 8.5zM8 18h.01M12 20h.01M16 18h.01M10 22h.01M14 22h.01" },
  fog: { label: "Brouillard", d: "M4 9h16M3 13h18M5 17h14M8 21h8" },
  wind: { label: "Vent", d: "M3 9h11a3 3 0 1 0-3-3M3 15h15a3 3 0 1 1-3 3M3 12h8" },
  mixed: { label: "Éclaircies", d: "M9 3v1.5M3.5 9H5M4.9 4.9l1 1M12.5 5.6A4 4 0 0 0 5.7 9.5M8 19a3.5 3.5 0 0 1-.5-7A5 5 0 0 1 17 11a3.7 3.7 0 0 1-.5 8z" },
};

/** The sky's glyph (stroke icon, 24 px grid). */
export function SkyIcon({ sky, className }: { sky: string; className?: string }) {
  const s = SKY[sky] ?? SKY.mixed!;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={s.label} className={className}>
      <path d={s.d} />
    </svg>
  );
}

/** French typography: "10 h", "64 €", "30 %" never break between the number and its unit. */
export function nb(text: string): string {
  return text.replace(/(\d) (?=h\b|h$|€|%|min\b)/gu, "$1\u00a0");
}

function tone(t: Tone): string {
  return t ?? "neutral";
}

/** An `ask` action: the words go to the conversation's composer (on screen, or once it opens). */
function useAsk(): (message: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (message: string) => {
      if (!composeInChat(message, false)) void navigate({ to: "/" });
    },
    [navigate],
  );
}

/**
 * The summary inside « Ta journée » (the brief bubble, V2 §6.2): a short line, then « heure · quoi » rows (the hour in a
 * 44 px column, accent when it matters), highlights as plain lines with their one action, figures as small insets.
 */
function useSummaryRegistry() {
  const navigate = useNavigate();
  const ask = useAsk();
  return useMemo(() => {
    const open = (to: unknown) => {
      if (typeof to === "string" && (SUMMARY_ROUTES as readonly string[]).includes(to)) void navigate({ to: to as SummaryRoute });
    };
    const askMessage = (message: unknown) => {
      if (typeof message === "string" && message.trim()) ask(message);
    };
    const { registry } = defineRegistry(summaryCatalog, {
      components: {
        Stack: ({ props, children }) => <div className={cx("flex min-w-0 flex-col", props.gap === "sm" ? "gap-1" : "gap-1.5")}>{children}</div>,
        Row: ({ children }) => <div className="flex min-w-0 flex-wrap gap-1.5 py-1">{children}</div>,
        Headline: ({ props }) => <p className="mb-0.5 text-[15px] leading-[1.45] text-muted wrap-anywhere">{nb(props.text)}</p>,
        DayTimeline: ({ props, children }) => (
          <ol aria-label={props.title ?? undefined} className="flex min-w-0 flex-col gap-0.5">
            {children}
          </ol>
        ),
        TimelineItem: ({ props }) => (
          <li className="flex min-w-0 gap-2.5 text-[15px] leading-snug">
            <span className={cx("w-11 shrink-0 tabular-nums", props.tone === "accent" || props.tone === "warning" ? "text-accent" : "text-muted")}>{nb(props.time)}</span>
            <span className="min-w-0 wrap-anywhere">
              {nb(props.label)}
              {props.note ? <span className="block text-[13px] text-subtle">{nb(props.note)}</span> : null}
            </span>
          </li>
        ),
        Highlight: ({ props, children }) => (
          <div className="mt-1 min-w-0 rounded-[12px] bg-inset px-2.5 py-2">
            <p className="text-[14.5px] leading-snug font-semibold wrap-anywhere">{nb(props.title)}</p>
            {props.text ? <p className="text-[13.5px] leading-snug text-muted wrap-anywhere">{nb(props.text)}</p> : null}
            {children ? <div className="mt-2 flex flex-wrap gap-2">{children}</div> : null}
          </div>
        ),
        Stat: ({ props }) => (
          <div className="flex min-w-[76px] flex-col rounded-[12px] bg-inset px-2.5 py-1.5">
            <span className={cx("font-display text-[20px] leading-none tabular-nums", STAT_TONE[tone(props.tone)])}>{props.value}</span>
            <span className="mt-0.5 text-[12px] leading-snug text-muted">{props.label}</span>
          </div>
        ),
        PersonChip: ({ props }) => (
          <div className="flex max-w-full min-w-0 items-center gap-2 rounded-full bg-inset py-1 pr-3 pl-1">
            <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-chip text-[12px] font-semibold">
              {props.name.trim().charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 truncate text-[13.5px]">
              <span className="font-semibold">{props.name}</span> <span className="text-muted">{props.why}</span>
            </span>
          </div>
        ),
        Weather: ({ props }) => (
          <div className="flex min-w-0 items-center gap-2 text-[14px]">
            <SkyIcon sky={props.sky} className="h-5 w-5 shrink-0 text-muted" />
            <span className="text-[17px] tabular-nums">{props.temp}</span>
            {props.hint ? <span className="min-w-0 text-muted wrap-anywhere">{props.hint}</span> : null}
          </div>
        ),
        Progress: ({ props }) => (
          <div className="min-w-0 py-0.5">
            <div className="flex items-baseline justify-between gap-3 text-[14px]">
              <span className="min-w-0 wrap-anywhere">{props.label}</span>
              <span className="shrink-0 text-muted tabular-nums">{props.percent} %</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-chip" role="progressbar" aria-valuenow={props.percent} aria-valuemin={0} aria-valuemax={100} aria-label={props.label}>
              <div className="h-full rounded-full bg-accent" style={{ width: `${props.percent}%` }} />
            </div>
          </div>
        ),
        ActionChip: ({ props, emit }) => (
          <button
            type="button"
            onClick={() => emit("press")}
            className="relative inline-flex h-11 max-w-full items-center rounded-full bg-chip px-3.5 text-[14px] text-fg transition hover:bg-[#333333] md:h-[34px] md:after:absolute md:after:inset-x-0 md:after:-inset-y-[5px] md:after:content-['']"
          >
            <span className="truncate">{props.label}</span>
          </button>
        ),
      },
      actions: {
        ask: async (params) => askMessage(params?.message),
        open: async (params) => open(params?.to),
      },
    });
    const handlers = {
      ask: (params: Record<string, unknown>) => askMessage(params.message),
      open: (params: Record<string, unknown>) => open(params.to),
    };
    return { registry, handlers };
  }, [navigate, ask]);
}

/** The spec of a summary, drawn (data-testid="pulse-summary", data-source = model | fallback). */
export function PulseSummaryView({ summary }: { summary: PulseSummary }) {
  const { registry, handlers } = useSummaryRegistry();
  return (
    <div data-testid="pulse-summary" data-source={summary.source} className="min-w-0">
      <JSONUIProvider registry={registry} handlers={handlers}>
        <Renderer spec={summary.spec as unknown as Spec} registry={registry} />
      </JSONUIProvider>
    </div>
  );
}

/** A calm placeholder while the summary is written. */
export function PulseSummarySkeleton() {
  const bar = "rounded-full bg-chip animate-pulse";
  return (
    <div role="status" aria-label="Préparation de ta journée" className="flex flex-col gap-2 py-1">
      <div className={cx(bar, "h-3.5 w-56")} />
      <div className={cx(bar, "h-3.5 w-44")} />
      <div className={cx(bar, "h-3.5 w-48")} />
    </div>
  );
}

/** Up to three suggestions above the composer (data-testid="suggestion-chip"): each one fills the composer. */
export function SuggestionChips({ summary, className }: { summary: PulseSummary | undefined; className?: string }): ReactNode {
  const ask = useAsk();
  if (!summary?.suggestions.length) return null;
  return (
    <div className={cx("flex gap-1.5 overflow-x-auto [scrollbar-width:none]", className)}>
      {summary.suggestions.slice(0, 3).map((s) => (
        <button
          key={s.label}
          type="button"
          data-testid="suggestion-chip"
          onClick={() => ask(s.message)}
          className="inline-flex h-11 max-w-[260px] shrink-0 items-center rounded-full bg-elev px-3.5 text-[13.5px] text-fg shadow-[inset_0_0_0_1px_var(--border)] transition hover:bg-chip md:h-9"
        >
          <span className="truncate">{s.label}</span>
        </button>
      ))}
    </div>
  );
}
